import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { PartyFilter } from "@/modules/core/list-cell-actions";
import { PendingInline } from "@/components/ui/pending-indicator";
import { listParties } from "@/shared/api/parties";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 220;

/**
 * In-chip party search for active filter pills — plan/30 chip edit.
 */
export function PartyFilterChipSearch(props: {
  companySlug: string;
  role: "customer" | "supplier";
  currentId: string;
  onPick: (party: PartyFilter | null) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [rows, setRows] = useState<PartyFilter[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 0 = All; 1..n = rows */
  const [activeIndex, setActiveIndex] = useState(0);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    setBusy(true);
    setError(null);
    listParties(props.companySlug, {
      role: props.role,
      q: debounced || undefined,
      limit: 20,
      signal: ac.signal,
    })
      .then((page) => {
        if (cancelled) return;
        setRows(
          page.parties.map((p) => ({
            id: p.id,
            name: p.name || p.code || p.id,
          })),
        );
        setActiveIndex(0);
      })
      .catch((err: unknown) => {
        if (cancelled || ac.signal.aborted) return;
        setRows([]);
        setError(err instanceof Error ? err.message : "Could not load");
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [props.companySlug, props.role, debounced]);

  const optionCount = 1 + rows.length;

  useEffect(() => {
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, rows]);

  function pickAll() {
    props.onPick(null);
    props.onClose();
  }

  function pickRow(p: PartyFilter) {
    props.onPick(p);
    props.onClose();
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % optionCount);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + optionCount) % optionCount);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex === 0) pickAll();
      else {
        const row = rows[activeIndex - 1];
        if (row) pickRow(row);
      }
    }
  }

  const noun = props.role === "customer" ? "customer" : "supplier";

  return (
    <div className="p-1">
      <div className="flex items-center justify-between gap-2 px-2 pb-1.5 pt-1">
        <div className="font-[family-name:var(--font-display)] text-xs font-semibold tracking-tight text-[var(--color-muted)]">
          Change {noun}
        </div>
        {busy && rows.length > 0 ? (
          <PendingInline className="text-[10px] text-[var(--color-muted)]">
            Updating…
          </PendingInline>
        ) : null}
      </div>
      <input
        className={cn(
          "mb-1.5 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)]",
          "px-2.5 py-2 text-sm text-[var(--color-fg)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
        )}
        placeholder={`Search ${noun}…`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onSearchKeyDown}
        autoFocus
        aria-controls="party-filter-chip-options"
      />
      {error ? (
        <p className="m-0 px-2 py-2 text-xs text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
      {busy && rows.length === 0 ? (
        <div className="px-2 py-3 text-xs text-[var(--color-muted)]">
          <PendingInline>Searching…</PendingInline>
        </div>
      ) : null}
      {!busy && rows.length === 0 && !error ? (
        <p className="m-0 px-2 py-3 text-xs text-[var(--color-muted)]">
          No matches
        </p>
      ) : null}
      <ul
        id="party-filter-chip-options"
        className="m-0 max-h-56 list-none space-y-0.5 overflow-y-auto p-0"
        role="listbox"
        aria-label={`${noun} options`}
      >
        <li className="m-0">
          <button
            ref={(el) => {
              optionRefs.current[0] = el;
            }}
            type="button"
            role="option"
            aria-selected={activeIndex === 0}
            className={cn(
              "flex w-full rounded-lg px-2.5 py-2 text-left text-sm",
              "text-[var(--color-muted)] hover:bg-[var(--color-primary)]/8",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
              activeIndex === 0 && "ring-2 ring-[var(--color-ring)]",
            )}
            onMouseEnter={() => setActiveIndex(0)}
            onClick={pickAll}
          >
            All {noun}s
          </button>
        </li>
        {rows.map((p, i) => {
          const selected = p.id === props.currentId;
          const active = activeIndex === i + 1;
          return (
            <li key={p.id} className="m-0">
              <button
                ref={(el) => {
                  optionRefs.current[i + 1] = el;
                }}
                type="button"
                role="option"
                aria-selected={selected}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm",
                  "hover:bg-[var(--color-primary)]/8",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
                  selected &&
                    "bg-[var(--color-primary)]/12 font-medium text-[var(--color-primary)]",
                  active && !selected && "bg-[var(--color-border)]/40",
                  active && "ring-2 ring-[var(--color-ring)]",
                )}
                onMouseEnter={() => setActiveIndex(i + 1)}
                onClick={() => pickRow(p)}
              >
                <span className="truncate">{p.name}</span>
                {selected ? (
                  <span className="text-xs opacity-70" aria-hidden>
                    ✓
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
